import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, downloadUrl } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { ArrowLeft, CalendarDays, Users, Ticket, FileText, Download, Lock } from "lucide-react";
import { ReplayPlayer } from "./ReplayPlayer";

const TYPE_LABELS = { webinaire: "Webinaire", seminaire: "Événement", congres: "Congrès" };

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export default function MemberSeminarDetail() {
  const { slug } = useParams();
  const [sem, setSem] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api(`/membre/seminaires/${slug}`).then(setSem).catch((e) => setError(e.message));
  }, [slug]);

  const videos = sem?.videos || [];

  return (
    <AppLayout>
      <div data-testid="seminar-detail-page" className="max-w-4xl mx-auto space-y-6 animate-fade-in">
        <Link to="/seminaires" data-testid="seminar-back-link"
          className="inline-flex items-center gap-1.5 text-sm text-cfhtb-secondary hover:text-cfhtb-primary transition-colors">
          <ArrowLeft className="h-4 w-4" /> Mes événements
        </Link>

        {error ? (
          <div className="bg-white rounded-lg border border-cfhtb-error/30 p-6 text-sm text-cfhtb-error" data-testid="seminar-error">{error}</div>
        ) : !sem ? (
          <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden">
              {sem.visuel && <img src={sem.visuel} alt="" className="w-full max-h-72 object-cover" />}
              <div className="p-6 sm:p-8">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="bg-cfhtb-bg text-cfhtb-primary px-2.5 py-1 rounded-full font-medium">
                    {TYPE_LABELS[sem.type] || sem.type}
                  </span>
                  {sem.inscrit && (
                    <span className="bg-cfhtb-success/10 text-cfhtb-success px-2.5 py-1 rounded-full font-medium" data-testid="seminar-inscrit-badge">
                      Inscrit{sem.present ? " · Présent" : ""}
                    </span>
                  )}
                  {sem.acces_replay && (
                    <span className="bg-cfhtb-primary/10 text-cfhtb-primary px-2.5 py-1 rounded-full font-medium" data-testid="seminar-replay-badge">
                      Replay disponible
                    </span>
                  )}
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl font-bold text-cfhtb-heading tracking-tight mt-3 leading-tight" data-testid="seminar-title">
                  {sem.titre}
                </h1>
                <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-cfhtb-text">
                  <span className="inline-flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-cfhtb-light" />
                    {formatDate(sem.date_debut)}
                  </span>
                  {sem.intervenants?.length > 0 && (
                    <span className="inline-flex items-center gap-2">
                      <Users className="h-4 w-4 text-cfhtb-light" />
                      {sem.intervenants.join(", ")}
                    </span>
                  )}
                </div>
                {sem.description && (
                  <p className="text-sm text-cfhtb-text leading-relaxed mt-4 whitespace-pre-line">{sem.description}</p>
                )}
              </div>
            </div>

            {videos.length > 0 ? (
              <ReplayPlayer videos={videos} titre={sem.titre} slug={slug} filigrane={sem.filigrane} />
            ) : (
              <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 flex items-start gap-4" data-testid="replay-locked">
                <div className="h-10 w-10 rounded-md bg-cfhtb-bg flex items-center justify-center shrink-0">
                  <Lock className="h-5 w-5 text-cfhtb-secondary" />
                </div>
                <div className="text-sm">
                  <p className="font-medium text-cfhtb-heading">
                    {sem.inscrit ? "Replay non disponible" : "Réservé aux inscrits"}
                  </p>
                  <p className="text-cfhtb-text mt-1">
                    {sem.inscrit
                      ? "Le replay n'est pas encore publié ou votre accès a expiré. Contactez la CFHTB si vous pensez qu'il s'agit d'une erreur."
                      : "Le replay de cet événement est accessible aux personnes inscrites."}
                  </p>
                  {!sem.inscrit && sem.lien_billetterie_externe && (
                    <a
                      href={sem.lien_billetterie_externe}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="billetterie-link"
                      className="inline-flex items-center gap-2 mt-3 bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors"
                    >
                      <Ticket className="h-4 w-4" /> Accéder à la billetterie
                    </a>
                  )}
                </div>
              </div>
            )}

            {sem.supports?.length > 0 && (
              <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6" data-testid="seminar-documents-list">
                <h2 className="font-serif text-xl font-semibold text-cfhtb-heading mb-4">Supports de l'événement</h2>
                <ul className="divide-y divide-slate-100">
                  {sem.supports.map((d) => (
                    <li key={d.id}>
                      <Link to={`/documents/${d.id}`} data-testid={`seminar-doc-${d.id}`}
                        className="py-3 flex items-center gap-3 hover:bg-cfhtb-bg/40 -mx-2 px-2 rounded transition-colors group">
                        <FileText className="h-4 w-4 text-cfhtb-light shrink-0 group-hover:text-cfhtb-primary transition-colors" />
                        <span className="text-sm font-medium text-cfhtb-heading group-hover:text-cfhtb-primary transition-colors">{d.titre}</span>
                        <Download className="h-3.5 w-3.5 text-cfhtb-light ml-auto" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
